import { useCallback, useEffect, useMemo } from 'react';

import { Input } from '@/lib/base/forms/input/input';

import { type SlugStatus, useSlugAvailability } from './useSlugAvailability';

interface SlugAvailabilityFieldProps {
  value: string;
  onChange: (next: string) => void;
  onStatusChange?: (status: SlugStatus) => void;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  /**
   * Override the message shown when the field is empty and idle. Defaults to
   * "Leave blank to auto-generate from name." which is appropriate for the
   * create form; edit surfaces should pass a context-appropriate string (or
   * empty string to show nothing) since auto-generation only happens at
   * creation time.
   */
  idleEmptyMessage?: string;
  /**
   * When provided, renders a live URL preview below the input showing the
   * full URL (e.g. "meet.ahoy.ai/your-slug") so users see what they're
   * creating. The slug input takes the full width for comfortable editing.
   */
  domainPrefix?: string;
}

const DEFAULT_IDLE_EMPTY_MESSAGE = 'Leave blank to auto-generate from name.';

export function SlugAvailabilityField({
  value,
  onChange,
  onStatusChange,
  disabled,
  placeholder,
  'aria-label': ariaLabel,
  idleEmptyMessage = DEFAULT_IDLE_EMPTY_MESSAGE,
  domainPrefix,
}: SlugAvailabilityFieldProps) {
  const status = useSlugAvailability(value);

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  const handleChange = useCallback(
    (next: string) => onChange(next),
    [onChange]
  );

  const statusMessage = useMemo((): string => {
    switch (status) {
      case 'idle':
        return value.length === 0 ? idleEmptyMessage : '';
      case 'checking':
        return 'Checking availability…';
      case 'available':
        return 'Available';
      case 'taken':
        return 'This URL is already in use.';
      case 'format-invalid':
        return 'Use 3–50 lowercase letters, numbers, or hyphens. Must start and end with a letter or number.';
      case 'reserved':
        return 'This slug is reserved.';
      case 'error':
        return 'Unable to check availability. Please try again.';
    }
  }, [status, value, idleEmptyMessage]);

  const statusClassName = useMemo((): string => {
    switch (status) {
      case 'available':
        return 'mt-1.5 text-sm text-[var(--color-text-success-primary)]';
      case 'taken':
      case 'format-invalid':
      case 'reserved':
      case 'error':
        return 'mt-1.5 text-sm text-[var(--color-text-error-primary)]';
      default:
        return 'mt-1.5 text-sm text-[var(--color-text-secondary)]';
    }
  }, [status]);

  const statusElement = (
    <p
      data-component='slug-status'
      data-state={status}
      data-testid='slug-status'
      className={statusClassName}
      role='status'
      aria-live='polite'
      aria-atomic='true'
    >
      {statusMessage}
    </p>
  );

  if (domainPrefix) {
    return (
      <div>
        <Input
          type='text'
          value={value}
          onChange={handleChange}
          isDisabled={disabled}
          placeholder={placeholder}
          aria-label={ariaLabel}
        />
        <p className='mt-2 text-sm text-[var(--color-text-secondary)] break-all'>
          <span>{domainPrefix}</span>
          {value && (
            <span className='font-medium text-[var(--color-text-primary)]'>
              {value}
            </span>
          )}
        </p>
        {statusElement}
      </div>
    );
  }

  return (
    <div>
      <Input
        type='text'
        value={value}
        onChange={handleChange}
        isDisabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
      {statusElement}
    </div>
  );
}

SlugAvailabilityField.displayName = 'SlugAvailabilityField';
